import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Helmet } from "react-helmet";
import axios from "axios";
import bg3   from "../images/blogs/Bg/bg-3.png";
import dots  from "../images/blogs/dots.svg";
import yellow from "../images/blogs/yellow.svg";
import { api_url } from "../config/config";

const BlogDetails = () => {
  const { slug } = useParams();
  const [blog,    setBlog]    = useState(null);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    const fetchBlog = async () => {
      try {
        /* API returns { success, message, data: [...] } */
        const res      = await axios.get(`${api_url}/blog`);
        const blogData = Array.isArray(res.data?.data) ? res.data.data : [];
        const found    = blogData.find((b) => b.slug === slug);
        if (found) setBlog(found);
        else        setError("Blog not found.");
      } catch {
        setError("Failed to fetch blog details.");
      } finally {
        setLoading(false);
      }
    };
    fetchBlog();
  }, [slug]);

  /* ── Loading ── */
  if (loading) return (
    <div className="min-h-screen bg-[#f8fafa] font-poppins flex flex-col gap-0">
      {/* Banner skeleton */}
      <div className="w-full h-[440px] sm:h-[520px] bg-[#e2eaea] animate-pulse" />
      {/* Content skeleton */}
      <div className="px-5 sm:px-10 lg:px-20 py-10 max-w-[860px] mx-auto w-full flex flex-col gap-5">
        {Array.from({ length: 6 }).map((_, i) => (
          <div key={i} className={`h-4 bg-[#e2eaea] rounded-full animate-pulse ${i === 0 ? "w-2/3" : i % 3 === 0 ? "w-1/2" : "w-full"}`} />
        ))}
      </div>
    </div>
  );

  /* ── Error ── */
  if (error) return (
    <div className="min-h-screen bg-[#f8fafa] font-poppins flex flex-col items-center justify-center gap-4 px-5">
      <div className="w-16 h-16 rounded-2xl bg-[#feecec] border border-[#f5b8b8] flex items-center justify-center">
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#c0392b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
        </svg>
      </div>
      <p className="text-[15px] font-semibold text-[#374747]">{error}</p>
      <Link to="/blogs" className="text-[13px] font-semibold text-[#1e4a46] hover:text-[#ff601e] transition-colors flex items-center gap-1">
        ← Back to blogs
      </Link>
    </div>
  );

  const formatDate = (d) =>
    new Date(d).toLocaleDateString("en-GB", { day: "2-digit", month: "long", year: "numeric" });

  const bannerImage = blog.blog_images?.[0]?.image_url || bg3;
  const secondImage = blog.blog_images?.[1]?.image_url || null;
  const readTime    = blog.content ? Math.max(1, Math.ceil(blog.content.length / 1000)) : 1;

  return (
    <>
      <Helmet>
        <title>{blog.title} | MediSYS Blog</title>
        <meta name="description" content={blog.subtitle || blog.title} />
      </Helmet>

      <div className="min-h-screen bg-[#f8fafa] font-poppins">

        {/* ══════════════════════════════════════════════
            HERO BANNER — full height image + overlay text
        ══════════════════════════════════════════════ */}
        <div className="relative w-full h-[420px] sm:h-[500px] lg:h-[560px] overflow-hidden">

          {/* Full cover image */}
          <img
            src={bannerImage}
            alt={blog.title}
            className="absolute inset-0 w-full h-full object-cover object-center"
            onError={(e) => { e.target.onerror = null; e.target.src = bg3; }}
          />

          {/* Dark gradient overlay — heavier at bottom */}
          <div className="absolute inset-0 bg-gradient-to-t from-[#0a1715]/95 via-[#0a1715]/50 to-[#0a1715]/20" />

          {/* Top breadcrumb */}
          <div className="absolute top-5 left-5 sm:left-10 lg:left-20 flex items-center gap-2 text-[12px] text-white/60 font-medium">
            <Link to="/blogs" className="hover:text-white transition-colors flex items-center gap-1">
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M19 12H5M12 5l-7 7 7 7"/>
              </svg>
              Blog
            </Link>
            <span className="text-white/30">/</span>
            <span className="text-white/60 truncate max-w-[200px]">{blog.title}</span>
          </div>

          {/* Bottom content — title, meta, all in one line / stack */}
          <div className="absolute bottom-0 left-0 right-0 px-5 sm:px-10 lg:px-20 pb-8 sm:pb-10">

            {/* Type badge */}
            {blog.blog_type?.name && (
              <span className="inline-block bg-[#ff601e] text-white text-[10px] font-bold px-3 py-1 rounded-full uppercase tracking-wide mb-3 shadow-[0_2px_8px_rgba(255,96,30,0.5)]">
                {blog.blog_type.name}
              </span>
            )}

            {/* Title */}
            <h1 className="font-bold text-white text-[22px] sm:text-[32px] lg:text-[42px] leading-tight max-w-[820px] mb-4">
              {blog.title}
            </h1>

            {/* Meta row — author · date · read time — ALL IN ONE LINE */}
            <div className="flex flex-wrap items-center gap-3 sm:gap-5">
              {/* Author */}
              <div className="flex items-center gap-2">
                <div className="w-7 h-7 rounded-full bg-[#1e4a46] border-2 border-[#5fbdb3]/40 flex items-center justify-center flex-shrink-0">
                  <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
                    <circle cx="12" cy="7" r="4"/>
                  </svg>
                </div>
                <span className="text-[13px] font-semibold text-white/90">{blog.author}</span>
              </div>

              <span className="text-white/25 text-[14px]">·</span>

              {/* Date */}
              <div className="flex items-center gap-1.5">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#5fbdb3" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="4" width="18" height="18" rx="2"/>
                  <line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/>
                </svg>
                <span className="text-[13px] text-white/70">{formatDate(blog.published_at)}</span>
              </div>

              <span className="text-white/25 text-[14px]">·</span>

              {/* Read time */}
              <div className="flex items-center gap-1.5">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#5fbdb3" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
                </svg>
                <span className="text-[13px] text-white/70">{readTime} min read</span>
              </div>
            </div>
          </div>
        </div>

        {/* ══════════════════════════════════════════════
            BLOG CONTENT
        ══════════════════════════════════════════════ */}
        <div className="px-5 sm:px-10 lg:px-20 py-10  mx-auto">

          {/* Subtitle */}
          {blog.subtitle && (
            <p className="text-[15px] sm:text-[17px] text-[#607d7b] leading-relaxed mb-8 font-medium border-l-4 border-[#5fbdb3] pl-4">
              {blog.subtitle}
            </p>
          )}

          {/* Second image */}
          {secondImage && (
            <div className="my-8 rounded-2xl overflow-hidden shadow-[0_6px_28px_rgba(22,44,42,0.12)] border border-[#e2eaea]">
              <img
                src={secondImage}
                alt={`${blog.title} — image 2`}
                className="w-full h-auto max-h-[420px] object-cover"
                onError={(e) => { e.target.onerror = null; e.target.src = "/default.svg"; }}
              />
            </div>
          )}

          {/* Body content */}
          {blog.content && (
            <div
              className="prose prose-lg max-w-none text-[#374747] leading-[1.9] text-[15px] sm:text-[16px]
                prose-headings:text-[#1e4a46] prose-headings:font-bold
                prose-a:text-[#ff601e] prose-a:no-underline hover:prose-a:underline
                prose-strong:text-[#1e4a46]
                prose-img:rounded-2xl prose-img:shadow-md"
              dangerouslySetInnerHTML={{ __html: blog.content }}
            />
          )}

          {/* Quote section */}
          {blog.quote && (
            <div
              className="relative my-12 rounded-2xl overflow-hidden p-8 sm:p-12 flex items-center justify-center bg-cover bg-center"
              style={{ backgroundImage: `url(${bg3})` }}
            >
              {/* Dark overlay */}
              <div className="absolute inset-0 bg-[#162c2a]/75" />
              <img src={dots}   className="absolute top-0 left-0 w-24 sm:w-32 opacity-60 pointer-events-none" alt="" />
              <img src={yellow} className="absolute bottom-0 left-0 w-16 sm:w-20 opacity-80 pointer-events-none z-10" alt="" />
              <blockquote className="relative z-10 italic text-[15px] sm:text-[20px] lg:text-[24px] font-semibold text-white text-center leading-relaxed max-w-[700px]">
                "{blog.quote}"
              </blockquote>
            </div>
          )}

          {/* Back button */}
          <div className="mt-10 pt-6 border-t border-[#e2eaea]">
            <Link
              to="/blogs"
              className="inline-flex items-center gap-2 bg-[#162c2a] hover:bg-[#1e4a46] text-white font-semibold text-[13px] px-6 h-10 rounded-xl transition-all duration-200 shadow-[0_2px_10px_rgba(22,44,42,0.25)] hover:-translate-y-0.5"
            >
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M19 12H5M12 5l-7 7 7 7"/>
              </svg>
              Back to Blog
            </Link>
          </div>
        </div>
      </div>
    </>
  );
};

export default BlogDetails;